import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import convert from "convert-units";
import { Formik, FormikErrors, FormikProps, FormikTouched } from "formik";
import {
  Box,
  Button,
  HStack,
  Icon,
  Image,
  Input,
  Modal,
  Pressable,
  ScrollView,
  Text,
  useTheme,
  VStack,
} from "native-base";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, SafeAreaView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Routes, Scale, VerticalScale } from "../../constants";
import { calculateMinimumHealthyWeight } from "../../helpers/fitnessCalculationHelpers";
import {
  getOrganisation,
  isDmaSubdomain,
  isFluffyToFitSubdomain,
  isFuelAndFlowSubdomain,
  isNateBowerSubdomain,
} from "../../helpers/userHelpers";
import type { PersonalWeightUnitEnum } from "../../services/backendTypes";
import {
  onboardingDataSelector,
  OnboardingState,
  setPhysicalStats,
  setPreferences,
} from "../../slices/onboardingSlice";
import { userSelector } from "../../slices/userSlice";
import { updateMacrosInState } from "./onboardingHelpers";

// Helper conversion function (moved outside component)
const convertCmToFtIn = (cm: number): { feet: number; inches: number } => {
  const totalInches = convert(cm).from("cm").to("in");
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches };
};

interface PhysicalStatsScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

// FIXME: Can this be taken from the model.py auto-generated types instead?
enum HeightUnit {
  CENTIMETRES = "cm",
  FEET_INCHES = "ftin",
}

interface DefaultUnits {
  heightUnit: HeightUnit;
  weightUnit: PersonalWeightUnitEnum;
}

// Helper function to determine default units specifically for the web platform
const getDefaultUnitsForWeb = (): DefaultUnits => {
  // Just hacking this for now because the locale is not working correctly otherwise
  if (isFuelAndFlowSubdomain() || isFluffyToFitSubdomain() || isNateBowerSubdomain() || isDmaSubdomain()) {
    return {
      heightUnit: HeightUnit.FEET_INCHES,
      weightUnit: "POUNDS",
    };
  }

  // Only run this logic on the web
  if (Platform.OS !== "web") {
    // Default to metric for non-web platforms (or adjust as needed)
    console.warn("getDefaultUnitsForWeb called on non-web platform, defaulting to metric.");
    return {
      heightUnit: HeightUnit.CENTIMETRES,
      weightUnit: "KILOGRAMS",
    };
  }

  let regionCode: string | undefined;

  try {
    // Use standard browser API navigator.language
    const browserLocale = navigator.language || "nl-NL"; // Provide a fallback to Dutch
    // Use standard Intl.Locale to parse and get the region
    regionCode = new Intl.Locale(browserLocale).region?.toUpperCase();
  } catch (e) {
    console.warn("Could not parse browser locale, defaulting to metric:", e);
    // Fallback to metric if parsing fails
    return {
      heightUnit: HeightUnit.CENTIMETRES,
      weightUnit: "KILOGRAMS",
    };
  }

  // Determine units based on region
  if (regionCode === "US") {
    return {
      heightUnit: HeightUnit.FEET_INCHES,
      weightUnit: "POUNDS",
    };
  }
  if (regionCode === "GB") {
    return {
      heightUnit: HeightUnit.FEET_INCHES,
      weightUnit: "STONES",
    };
  }
  // Default to metric for all other regions or if regionCode is undefined
  return {
    heightUnit: HeightUnit.CENTIMETRES,
    weightUnit: "KILOGRAMS",
  };
};

interface WeightUnitModalProps {
  visible: boolean;
  onClose: () => void;
  currentUnit: PersonalWeightUnitEnum;
  onSelect: (unit: PersonalWeightUnitEnum) => void;
  currentKg: number;
  currentGoalKg: number;
  setFieldValue: FormikProps<{
    heightInCm: number;
    weightInKg: number;
    goalWeight: number;
  }>["setFieldValue"];
}

// Helper conversion functions using convert-units (defined within component scope)
const convertFtInToCm = (ftStr: string, inchesStr: string): number => {
  const ftNum = parseFloat(ftStr) || 0;
  const inNum = parseFloat(inchesStr) || 0;
  const totalInches = ftNum * 12 + inNum;
  return Math.round(convert(totalInches).from("in").to("cm"));
};

const convertKgToLbs = (kg: number): number => convert(kg).from("kg").to("lb");

const convertLbsToKg = (lbs: number): number => convert(lbs).from("lb").to("kg");

const convertStonesToKg = (stones: string, pounds: string): number => {
  const totalPounds = (parseFloat(stones) || 0) * 14 + (parseFloat(pounds) || 0);
  return convert(totalPounds).from("lb").to("kg");
};

const convertKgToStonesPounds = (kg: number): { stones: string; pounds: string } => {
  const totalPounds = convert(kg).from("kg").to("lb");
  const stones = Math.floor(totalPounds / 14);
  const pounds = Math.round(totalPounds % 14);
  return { stones: String(stones), pounds: String(pounds) };
};

const PhysicalStatsScreen: React.FC<PhysicalStatsScreenProps> = ({ navigation }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const onboardingData = useSelector(onboardingDataSelector);

  const user = useSelector(userSelector);
  const organisation = user ? getOrganisation(user) : undefined;
  const logoUri = organisation?.logo;

  // Determine default units using the web-specific helper
  // This now correctly handles the web-only focus
  const { heightUnit: defaultHeightUnit, weightUnit: defaultWeightUnit } = getDefaultUnitsForWeb();

  // Initialize state with the determined defaults
  const [heightUnit, setHeightUnit] = useState<HeightUnit>(defaultHeightUnit);
  const [weightUnit, setWeightUnit] = useState<PersonalWeightUnitEnum>(defaultWeightUnit);
  const [weightUnitModalVisible, setWeightUnitModalVisible] = useState(false);

  // Define initial metric values from onboarding data or defaults
  const initialCm = onboardingData.physicalStats?.heightInCm || 160;
  const initialKg = onboardingData.physicalStats?.weightInKg || 70;
  const initialGoalKg = onboardingData.physicalStats?.goalWeight || 65;

  // Convert initial metric values for potential use in imperial state initialization
  const { feet: initialFtNum, inches: initialInNum } = convertCmToFtIn(initialCm);
  const { stones: initialStonesStr, pounds: initialLbsRemainderStr } = convertKgToStonesPounds(initialKg);
  const { stones: initialGoalStonesStr, pounds: initialGoalLbsRemainderStr } = convertKgToStonesPounds(initialGoalKg);

  // Create variables to store the appropriate error messages based on the current unit system
  const heightMinErrorMessage =
    heightUnit === HeightUnit.CENTIMETRES
      ? t("onboarding.onboarding_5_physical_stats_screen.min_height")
      : t("onboarding.onboarding_5_physical_stats_screen.min_height_imperial");

  const heightMaxErrorMessage =
    heightUnit === HeightUnit.CENTIMETRES
      ? t("onboarding.onboarding_5_physical_stats_screen.max_height")
      : t("onboarding.onboarding_5_physical_stats_screen.max_height_imperial");

  const weightMinErrorMessage =
    weightUnit === "KILOGRAMS"
      ? t("onboarding.onboarding_5_physical_stats_screen.min_weight")
      : t("onboarding.onboarding_5_physical_stats_screen.min_weight_imperial");

  const weightMaxErrorMessage =
    weightUnit === "KILOGRAMS"
      ? t("onboarding.onboarding_5_physical_stats_screen.max_weight")
      : t("onboarding.onboarding_5_physical_stats_screen.max_weight_imperial");

  const goalWeightMaxErrorMessage =
    weightUnit === "KILOGRAMS"
      ? t("onboarding.onboarding_5_physical_stats_screen.max_goal_weight")
      : t("onboarding.onboarding_5_physical_stats_screen.max_goal_weight_imperial");

  // Validation schema that uses the unit-appropriate error messages
  const validationSchema = Yup.object().shape({
    heightInCm: Yup.number()
      .required(t("onboarding.onboarding_5_physical_stats_screen.valid_height"))
      .positive(t("onboarding.onboarding_5_physical_stats_screen.positive_height"))
      .min(120, heightMinErrorMessage)
      .max(250, heightMaxErrorMessage),
    weightInKg: Yup.number()
      .required(t("onboarding.onboarding_5_physical_stats_screen.valid_weight"))
      .positive(t("onboarding.onboarding_5_physical_stats_screen.positive_weight"))
      .min(45, weightMinErrorMessage)
      .max(300, weightMaxErrorMessage),
    goalWeight: Yup.number()
      .required(t("onboarding.onboarding_5_physical_stats_screen.valid_goal_weight"))
      .positive(t("onboarding.onboarding_5_physical_stats_screen.positive_goal_weight"))
      .test("minGoalWeight", t("onboarding.onboarding_5_physical_stats_screen.min_goal_weight"), function (value) {
        const { heightInCm } = this.parent;
        return (value || 0) >= calculateMinimumHealthyWeight(heightInCm as number);
      })
      .max(300, goalWeightMaxErrorMessage),
  });

  type FormSchema = Yup.InferType<typeof validationSchema>;

  // Initialize imperial input state based on default units
  const [heightFt, setHeightFt] = useState<string>(
    defaultHeightUnit === HeightUnit.FEET_INCHES ? String(initialFtNum) : ""
  );
  const [heightInches, setHeightInches] = useState<string>(
    defaultHeightUnit === HeightUnit.FEET_INCHES ? String(initialInNum) : ""
  );

  const [weightStones, setWeightStones] = useState<string>(defaultWeightUnit === "STONES" ? initialStonesStr : "");
  const [weightPounds, setWeightPounds] = useState<string>(
    defaultWeightUnit === "STONES" ? initialLbsRemainderStr : ""
  );

  const [goalWeightStones, setGoalWeightStones] = useState<string>(
    defaultWeightUnit === "STONES" ? initialGoalStonesStr : ""
  );
  const [goalWeightPounds, setGoalWeightPounds] = useState<string>(
    defaultWeightUnit === "STONES" ? initialGoalLbsRemainderStr : ""
  );

  const onSubmit = (values: FormSchema): void => {
    dispatch(setPhysicalStats(values));

    // Determine if imperial units should be used for recipes based on the final selected weight unit
    const shouldUseImperialForRecipes = weightUnit === "POUNDS";

    dispatch(
      setPreferences({
        ...onboardingData.preferences,
        usesImperialUnitsForRecipes: shouldUseImperialForRecipes,
        personalWeightUnit: weightUnit,
        usesImperialUnitsForHeight: heightUnit === HeightUnit.FEET_INCHES,
      })
    );

    const updatedOnboardingData: OnboardingState = {
      ...onboardingData,
      physicalStats: values,
    };

    updateMacrosInState(dispatch, updatedOnboardingData);

    // If the goal is weight loss then we need to know their target for weight loss
    // FIXME: Ensure that the logic that is currently done on the weekly goal screen is
    // done here if need be (or at least triggered)
    const nextScreen = onboardingData.goal === "LOSE_WEIGHT" ? Routes.WeeklyGoalScreen : Routes.NutritionalPlanScreen;
    navigation.navigate(nextScreen);
  };

  const getErrorMessage = (
    isGoalWeight: boolean,
    errors: FormikErrors<FormSchema>,
    touched: FormikTouched<FormSchema>
  ): string => {
    if (isGoalWeight) {
      return errors.goalWeight && touched.goalWeight ? errors.goalWeight : "";
    }
    return errors.weightInKg && touched.weightInKg ? errors.weightInKg : "";
  };

  const WeightUnitModal: React.FC<WeightUnitModalProps> = ({
    visible,
    onClose,
    currentUnit,
    onSelect,
    currentKg,
    currentGoalKg,
    setFieldValue,
  }) => {
    const handleUnitSelection = (selectedUnit: PersonalWeightUnitEnum): void => {
      const currentKgValue = parseFloat(String(currentKg)) || 0;
      const currentGoalKgValue = parseFloat(String(currentGoalKg)) || 0;
      const stonesAndPounds = convertKgToStonesPounds(currentKgValue);
      const goalStonesAndPounds = convertKgToStonesPounds(currentGoalKgValue);

      // Handle unit conversion based on selection
      if (selectedUnit === "POUNDS") {
        // No need to do anything special for pounds
      } else if (selectedUnit === "STONES") {
        setWeightStones(stonesAndPounds.stones);
        setWeightPounds(stonesAndPounds.pounds);
        setGoalWeightStones(goalStonesAndPounds.stones);
        setGoalWeightPounds(goalStonesAndPounds.pounds);
      } else if (selectedUnit === "KILOGRAMS") {
        // Round to 1 decimal place for kg
        setFieldValue("weightInKg", Math.round(currentKgValue * 10) / 10);
        setFieldValue("goalWeight", Math.round(currentGoalKgValue * 10) / 10);
      }

      onSelect(selectedUnit);
      onClose();
    };

    return (
      <Modal isOpen={visible} onClose={onClose}>
        <Modal.Content maxWidth="400px">
          <Modal.CloseButton />
          <Modal.Header>
            <Text fontSize="18" fontWeight="bold">
              {t("onboarding.onboarding_5_physical_stats_screen.select_weight_unit")}
            </Text>
          </Modal.Header>
          <Modal.Body>
            <VStack space={3}>
              <Pressable onPress={() => handleUnitSelection("KILOGRAMS")} testID="weight-unit-option-KILOGRAMS">
                <HStack space={3} alignItems="center" py={2}>
                  <Box
                    size={5}
                    borderRadius="full"
                    borderWidth={2}
                    borderColor={currentUnit === "KILOGRAMS" ? "primary.50" : "function.20"}
                    bg={currentUnit === "KILOGRAMS" ? "primary.50" : "transparent"}
                    alignItems="center"
                    justifyContent="center"
                  >
                    {currentUnit === "KILOGRAMS" && <Box size={2} borderRadius="full" bg="white" />}
                  </Box>
                  <Text>{t("general.kilograms")}</Text>
                </HStack>
              </Pressable>
              <Pressable onPress={() => handleUnitSelection("POUNDS")} testID="weight-unit-option-POUNDS">
                <HStack space={3} alignItems="center" py={2}>
                  <Box
                    size={5}
                    borderRadius="full"
                    borderWidth={2}
                    borderColor={currentUnit === "POUNDS" ? "primary.50" : "function.20"}
                    bg={currentUnit === "POUNDS" ? "primary.50" : "transparent"}
                    alignItems="center"
                    justifyContent="center"
                  >
                    {currentUnit === "POUNDS" && <Box size={2} borderRadius="full" bg="white" />}
                  </Box>
                  <Text>{t("general.pounds")}</Text>
                </HStack>
              </Pressable>
              <Pressable onPress={() => handleUnitSelection("STONES")} testID="weight-unit-option-STONES">
                <HStack space={3} alignItems="center" py={2}>
                  <Box
                    size={5}
                    borderRadius="full"
                    borderWidth={2}
                    borderColor={currentUnit === "STONES" ? "primary.50" : "function.20"}
                    bg={currentUnit === "STONES" ? "primary.50" : "transparent"}
                    alignItems="center"
                    justifyContent="center"
                  >
                    {currentUnit === "STONES" && <Box size={2} borderRadius="full" bg="white" />}
                  </Box>
                  <Text>{t("general.stones")}</Text>
                </HStack>
              </Pressable>
            </VStack>
          </Modal.Body>
          <Modal.Footer>
            <Button onPress={onClose} variant="outline">
              {t("general.close")}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    );
  };

  return (
    <Formik
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initialValues={validationSchema.cast({
        heightInCm: initialCm, // Use pre-calculated initial metric value
        weightInKg: initialKg, // Use pre-calculated initial metric value
        goalWeight: initialGoalKg, // Use pre-calculated initial metric value
      })}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, setFieldValue, values, errors, touched }) => {
        const toggleHeightUnit = (): void => {
          if (heightUnit === HeightUnit.CENTIMETRES) {
            // Convert current cm value to ft/in using convert-units
            const currentCm = parseFloat(String(values.heightInCm)) || 0;
            const totalInches = convert(currentCm).from("cm").to("in");
            const feet = Math.floor(totalInches / 12);
            const inchesVal = Math.round(totalInches % 12);
            setHeightFt(String(feet));
            setHeightInches(String(inchesVal));
            setHeightUnit(HeightUnit.FEET_INCHES);
          } else {
            setHeightUnit(HeightUnit.CENTIMETRES);
          }
        };

        const openWeightUnitModal = (): void => {
          setWeightUnitModalVisible(true);
        };

        const renderWeightInput = (isGoalWeight: boolean = false): JSX.Element => {
          let weightInputElement: JSX.Element;

          switch (weightUnit) {
            case "POUNDS":
              weightInputElement = (
                <HStack alignItems="center" justifyContent="center" space={2} width="100%">
                  <Input
                    placeholder="Weight in pounds"
                    value={String(Math.round(convertKgToLbs(isGoalWeight ? values.goalWeight : values.weightInKg)))}
                    onChangeText={(text) => {
                      const kg = convertLbsToKg(parseFloat(text) || 0);
                      setFieldValue(isGoalWeight ? "goalWeight" : "weightInKg", kg);
                    }}
                    keyboardType="numeric"
                    flex={1}
                    maxWidth="70%"
                    textAlign="center"
                    borderColor="function.20"
                    _focus={{ borderColor: "primary.50" }}
                    selectTextOnFocus
                    testID={isGoalWeight ? "goal-weight-pounds-input" : "weight-pounds-input"}
                  />
                  <Text fontSize="16" color="function.30">
                    lb
                  </Text>
                </HStack>
              );
              break;
            case "STONES":
              weightInputElement = (
                <HStack space={2} justifyContent="center" alignItems="flex-start" width="100%">
                  <VStack flex={1} maxWidth="45%">
                    <Input
                      placeholder="Stones"
                      value={isGoalWeight ? goalWeightStones : weightStones}
                      onChangeText={(text) => {
                        if (isGoalWeight) {
                          setGoalWeightStones(text);
                          const kg = convertStonesToKg(text, goalWeightPounds);
                          setFieldValue("goalWeight", kg);
                        } else {
                          setWeightStones(text);
                          const kg = convertStonesToKg(text, weightPounds);
                          setFieldValue("weightInKg", kg);
                        }
                      }}
                      keyboardType="numeric"
                      textAlign="center"
                      borderColor="function.30"
                      _focus={{ borderColor: "primary.50" }}
                      InputRightElement={
                        <Text fontSize="16" color="function.30" mr={3}>
                          st
                        </Text>
                      }
                      testID={isGoalWeight ? "goal-weight-stones-input" : "weight-stones-input"}
                    />
                  </VStack>
                  <VStack flex={1} maxWidth="45%">
                    <Input
                      placeholder="Pounds"
                      value={isGoalWeight ? goalWeightPounds : weightPounds}
                      onChangeText={(text) => {
                        if (isGoalWeight) {
                          setGoalWeightPounds(text);
                          const kg = convertStonesToKg(goalWeightStones, text);
                          setFieldValue("goalWeight", kg);
                        } else {
                          setWeightPounds(text);
                          const kg = convertStonesToKg(weightStones, text);
                          setFieldValue("weightInKg", kg);
                        }
                      }}
                      keyboardType="numeric"
                      textAlign="center"
                      borderColor="function.30"
                      _focus={{ borderColor: "primary.50" }}
                      InputRightElement={
                        <Text fontSize="16" color="function.30" mr={3}>
                          lb
                        </Text>
                      }
                      testID={isGoalWeight ? "goal-weight-pounds-remainder-input" : "weight-pounds-remainder-input"}
                    />
                  </VStack>
                </HStack>
              );
              break;
            case "KILOGRAMS":
            default:
              weightInputElement = (
                <HStack alignItems="center" justifyContent="center" space={2} width="100%">
                  <Input
                    placeholder={t(
                      isGoalWeight
                        ? "onboarding.onboarding_5_physical_stats_screen.goal_weight_placeholder"
                        : "onboarding.onboarding_5_physical_stats_screen.weight_placeholder"
                    )}
                    value={String(isGoalWeight ? values.goalWeight : values.weightInKg)}
                    onChangeText={(text) => setFieldValue(isGoalWeight ? "goalWeight" : "weightInKg", text)}
                    keyboardType="decimal-pad"
                    flex={1}
                    maxWidth="70%"
                    textAlign="center"
                    borderColor="function.20"
                    _focus={{ borderColor: "primary.50" }}
                    selectTextOnFocus
                    testID={isGoalWeight ? "goal-weight-input" : "weight-input"}
                  />
                  <Text fontSize="16" color="function.30">
                    kg
                  </Text>
                </HStack>
              );
              break;
          }

          // Add error message display
          const errorMsg = getErrorMessage(isGoalWeight, errors, touched);
          if (errorMsg) {
            return (
              <VStack>
                {weightInputElement}
                <Text color="red.500" textAlign="center" mt={1}>
                  {errorMsg}
                </Text>
              </VStack>
            );
          }
          return weightInputElement;
        };

        return (
          <SafeAreaView style={{ flex: 1 }}>
            <Box flex={1} bg="primary.10">
              <VStack flex={1}>
                {/* Header */}
                <HStack alignItems="center" p={4}>
                  <Pressable onPress={() => navigation.goBack()}>
                    <Icon as={Ionicons} name="arrow-back" size="6" color="function.50" />
                  </Pressable>
                </HStack>

                {/* Content */}
                <ScrollView flex={1}>
                  <VStack alignItems="center" p={4} space={4}>
                    {/* Logo or Icon */}
                    {logoUri ? (
                      <Image
                        source={{ uri: logoUri }}
                        style={{ width: Scale(160), height: VerticalScale(60) }}
                        resizeMode="contain"
                        alt="Organization Logo"
                      />
                    ) : (
                      <Icon as={MaterialCommunityIcons} name="robot" size="16" color="primary.50" />
                    )}

                    <Text fontSize="24" fontWeight="bold" textAlign="center">
                      {t("onboarding.onboarding_5_physical_stats_screen.title")}
                    </Text>
                    <Text fontSize="16" color="function.30" textAlign="center">
                      {t("onboarding.onboarding_5_physical_stats_screen.subtitle")}
                    </Text>

                    {/* Height Section */}
                    <Text fontSize="24" fontWeight="bold" textAlign="center">
                      <Text fontSize="31">📏 </Text>
                      {t("onboarding.onboarding_5_physical_stats_screen.height_title")}
                    </Text>

                    {/* Height Input */}
                    {heightUnit === HeightUnit.CENTIMETRES ? (
                      <HStack alignItems="center" justifyContent="center" space={2} width="100%">
                        <Input
                          placeholder={t("onboarding.onboarding_5_physical_stats_screen.height_placeholder")}
                          value={String(values.heightInCm)}
                          onChangeText={(text) => setFieldValue("heightInCm", text)}
                          keyboardType="numeric"
                          flex={1}
                          maxWidth="70%"
                          textAlign="center"
                          borderColor="function.30"
                          _focus={{ borderColor: "primary.50" }}
                          selectTextOnFocus
                          testID="height-input"
                        />
                        <Text fontSize="16" color="function.30">
                          {t("onboarding.onboarding_5_physical_stats_screen.centimeters")}
                        </Text>
                      </HStack>
                    ) : (
                      <HStack space={2} justifyContent="center" alignItems="flex-start" width="100%">
                        <VStack flex={1} maxWidth="45%">
                          <Input
                            placeholder="Feet"
                            value={heightFt}
                            onChangeText={(text) => {
                              setHeightFt(text);
                              const newCm = convertFtInToCm(text, heightInches);
                              setFieldValue("heightInCm", newCm);
                            }}
                            keyboardType="numeric"
                            textAlign="center"
                            borderColor="function.30"
                            _focus={{ borderColor: "primary.50" }}
                            InputRightElement={
                              <Text fontSize="16" color="function.30" mr={3}>
                                ft
                              </Text>
                            }
                            testID="height-feet-input"
                          />
                        </VStack>
                        <VStack flex={1} maxWidth="45%">
                          <Input
                            placeholder="Inches"
                            value={heightInches}
                            onChangeText={(text) => {
                              setHeightInches(text);
                              const newCm = convertFtInToCm(heightFt, text);
                              setFieldValue("heightInCm", newCm);
                            }}
                            keyboardType="numeric"
                            textAlign="center"
                            borderColor="function.30"
                            _focus={{ borderColor: "primary.50" }}
                            InputRightElement={
                              <Text fontSize="16" color="function.30" mr={3}>
                                in
                              </Text>
                            }
                            testID="height-inches-input"
                          />
                        </VStack>
                      </HStack>
                    )}

                    {/* Toggle Height Unit Link */}
                    <Pressable onPress={toggleHeightUnit} testID="toggle-height-unit">
                      <Text color="blue.500" textDecorationLine="underline">
                        {heightUnit === HeightUnit.CENTIMETRES
                          ? t("onboarding.onboarding_5_physical_stats_screen.change_units_to_imperial")
                          : t("onboarding.onboarding_5_physical_stats_screen.change_units_to_metric")}
                      </Text>
                    </Pressable>

                    {/* Height Error Message */}
                    {errors.heightInCm && touched.heightInCm ? (
                      <Text color="red.500" textAlign="center">
                        {errors.heightInCm}
                      </Text>
                    ) : null}

                    {/* Weight Section */}
                    <Text fontSize="24" fontWeight="bold" textAlign="center" mt={4}>
                      <Text fontSize="31">⚖️ </Text>
                      {t("onboarding.onboarding_5_physical_stats_screen.weight_title")}
                    </Text>
                    <Text fontSize="16" color="function.30" textAlign="center">
                      {t("onboarding.onboarding_5_physical_stats_screen.weight_subtitle")}
                    </Text>

                    {/* Weight Input */}
                    {renderWeightInput(false)}

                    {/* Weight Unit Change Button */}
                    <Button
                      onPress={openWeightUnitModal}
                      variant="outline"
                      borderColor="primary.50"
                      _text={{ color: "primary.50" }}
                      testID="change-weight-unit-button"
                    >
                      {t("onboarding.onboarding_5_physical_stats_screen.change_units_for_weight")}
                    </Button>

                    {/* Weight Unit Modal */}
                    <WeightUnitModal
                      visible={weightUnitModalVisible}
                      onClose={() => setWeightUnitModalVisible(false)}
                      currentUnit={weightUnit}
                      onSelect={setWeightUnit}
                      currentKg={values.weightInKg}
                      currentGoalKg={values.goalWeight}
                      setFieldValue={setFieldValue}
                    />

                    {/* Goal Weight Section */}
                    <Text fontSize="24" fontWeight="bold" textAlign="center" mt={4}>
                      <Text fontSize="31">🎯 </Text>
                      {t("onboarding.onboarding_5_physical_stats_screen.goal_weight_title")}
                    </Text>
                    <Text fontSize="16" color="function.30" textAlign="center">
                      {t("onboarding.onboarding_5_physical_stats_screen.goal_weight_subtitle")}
                    </Text>

                    {/* Goal Weight Input */}
                    {renderWeightInput(true)}

                    {/* Next Button */}
                    <Button
                      onPress={() => handleSubmit()}
                      bg="primary.50"
                      _pressed={{ bg: "primary.50:alpha.80" }}
                      py={3}
                      px={8}
                      mt={6}
                      mb={6}
                      borderRadius={theme.borderRadii.button.default}
                      width="80%"
                      testID="next-button"
                    >
                      <Text color="primary.10" fontWeight="bold">
                        {t("onboarding.onboarding_5_physical_stats_screen.next_button")}
                      </Text>
                    </Button>
                  </VStack>
                </ScrollView>
              </VStack>
            </Box>
          </SafeAreaView>
        );
      }}
    </Formik>
  );
};

export default PhysicalStatsScreen;
